// The TEAM PEOPLE RAIL (Signal) — one row per User in the directory projection: the tonal initials
// avatar, the display name, and what the person actually does (or the suspended status —
// a suspended person stays VISIBLE, dimmed, never hidden: hiding is not the control, G-180). The rail
// renders the `identity` (A) directory; it exposes NO credential value and NO raw address (G-140/G-058)
// — the User view carries none. Selection is client view-state owned by the surface. Tokens only (G-181).

import { Avatar } from '../../app/Avatar.tsx';
import { ownerInitials } from '../owner-display.ts';
import type { UserRow } from '../know-projections.ts';

export function TeamPeopleRail({
  users,
  selectedId,
  paneSwapped,
  onSelect,
}: {
  readonly users: readonly UserRow[];
  readonly selectedId: string | null;
  /** True while the right pane shows something other than the person detail (queue / add pane) —
   *  the rail then marks NO row current, mirroring what the reader actually sees. */
  readonly paneSwapped: boolean;
  readonly onSelect: (userId: string) => void;
}): React.JSX.Element {
  return (
    <aside className="tm-rail" aria-label="People">
      <div className="mono-label tm-rail-label">People</div>
      {users.length === 0 ? (
        <div className="empty-line">No people in the directory yet.</div>
      ) : (
        users.map((user) => {
          const on = !paneSwapped && user.id === selectedId;
          const suspended = user.status === 'suspended';
          return (
            <button
              key={user.id}
              type="button"
              data-directory-row={user.id}
              aria-pressed={on}
              onClick={() => onSelect(user.id)}
              className={suspended ? 'tm-person suspended' : 'tm-person'}
            >
              <Avatar initials={ownerInitials(user.displayName)} />
              <span className="tm-person-main">
                <span className="tm-person-name">{user.displayName}</span>
                <span className="tm-person-sub">
                  {suspended
                    ? 'Suspended'
                    : user.responsibilities.trim().length > 0
                      ? user.responsibilities
                      : 'What they do not set'}
                </span>
              </span>
            </button>
          );
        })
      )}
    </aside>
  );
}
